import { format } from "date-fns"
import { CalendarIcon, X } from "lucide-react"

import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@f2e/ui/components/base"
import { isBeforeDateOnly } from "@f2e/ui/lib/date"
import { cn } from "@f2e/ui/lib/utils"

export interface ApiKeyExpirationDatePickerProps {
  date?: Date
  onSelect?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  fromDate?: Date
}

// Workspace API key dialog specific wrapper:
// keeps shadcn base DatePicker unchanged while allowing local behavior tweaks.
export const ApiKeyExpirationDatePicker = ({
  date,
  onSelect,
  placeholder = "Pick a date",
  disabled = false,
  fromDate,
}: ApiKeyExpirationDatePickerProps) => {
  return (
    <div className="relative">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-slate-500 dark:text-slate-400"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, "PPP") : <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        {/* Explicit popover placement is required by this dialog's layout. */}
        <PopoverContent className="w-auto p-0" side="bottom" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={onSelect}
            disabled={(candidateDate) =>
              isBeforeDateOnly(candidateDate, fromDate)
            }
            initialFocus
          />
        </PopoverContent>
      </Popover>

      {date && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0 hover:bg-slate-100 dark:hover:bg-slate-800"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onSelect?.(undefined)
          }}
          disabled={disabled}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
